"use client";
import { KeyboardEvent, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";

export default function CookieBanner() {
  const t = useTranslations('cookies');
  const [isVisible, setIsVisible] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const acceptButtonRef = useRef<HTMLButtonElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const cookiesAccepted = localStorage.getItem("cookiesAccepted");

    if (cookiesAccepted !== "true") {
      setIsVisible(true);
    }

    setIsLoaded(true);
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const activeElement = document.activeElement;
    previousFocusRef.current =
      activeElement instanceof HTMLElement && !dialogRef.current?.contains(activeElement)
        ? activeElement
        : null;

    acceptButtonRef.current?.focus();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible || !dialogRef.current) return;

    const changedElements: Array<{
      element: HTMLElement;
      inert?: boolean;
      ariaHidden: string | null;
    }> = [];

    let currentElement: HTMLElement | null = dialogRef.current;

    while (currentElement?.parentElement) {
      Array.from(currentElement.parentElement.children).forEach((sibling) => {
        if (!(sibling instanceof HTMLElement) || sibling === currentElement) return;

        changedElements.push({
          element: sibling,
          inert: sibling.inert,
          ariaHidden: sibling.getAttribute("aria-hidden"),
        });

        sibling.inert = true;
        sibling.setAttribute("aria-hidden", "true");
      });

      if (currentElement.parentElement === document.body) break;
      currentElement = currentElement.parentElement;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      changedElements.forEach(({ element, inert, ariaHidden }) => {
        element.inert = Boolean(inert);

        if (ariaHidden === null) {
          element.removeAttribute("aria-hidden");
        } else {
          element.setAttribute("aria-hidden", ariaHidden);
        }
      });

      document.body.style.overflow = previousOverflow;
    };
  }, [isVisible]);

  const closeDialog = (acceptCookies: boolean) => {
    if (acceptCookies) {
      localStorage.setItem("cookiesAccepted", "true");
      activateCookies();
    }

    setIsVisible(false);

    window.requestAnimationFrame(() => {
      previousFocusRef.current?.focus();
    });
  };

  const handleAccept = () => {
    closeDialog(true);
  };

  const activateCookies = () => {};

  const handleDialogKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      closeDialog(false);
      return;
    }

    if (event.key !== "Tab" || !dialogRef.current) return;

    const focusableElements = dialogRef.current.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    if (!focusableElements.length) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  };

  if (!isLoaded) return null;

  if (!isVisible) return null;

  return (
    <>
      <div className="fixed inset-0 z-[9998] bg-black/40" aria-hidden="true" />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-dialog-title"
        aria-describedby="cookie-dialog-description"
        onKeyDown={handleDialogKeyDown}
        className="fixed bottom-0 left-0 right-0 border-t-8 border-primary-300 z-[9999] bg-primary-700"
      >
        <div className="container py-32 md:[44px] xl:py-64 mx-auto flex flex-col md:flex-row justify-between gap-24 items-center">
          <div className="md:col-span-1">
            <h2 id="cookie-dialog-title" className="text-m-bold xl:text-l-bold text-white">
              {t("cookiesUse")}
            </h2>
            <span id="cookie-dialog-description" className="text-m-regular xl:text-l-regular text-white">
              {t("terms")} &nbsp;
              <Link
                href={"/avisos-legais" as any}
                className="text-m-regular xl:text-l-regular text-white underline"
                target="_blank"
              >
                {t("politc")}
              </Link>
            </span>
          </div>

          <div className=" flex w-full md:w-auto text-center items-center justify-end">
            <button
              ref={acceptButtonRef}
              type="button"
              className="bg-primary-600 w-full text-white xs:justify-center rounded-4 p-16 !hover:bg-primary-600/10"
              onClick={handleAccept}
            >
              {t("accept")}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
